<template>
  <div class="flex flex-col gap-6 md:gap-12">
    <div class="col-span-full grid gap-y-6 md:grid-cols-2 xl:col-span-1 xl:grid-cols-4">
      <div v-if="selectedProduct" class="flex flex-col gap-y-4">
        <CommonLabel
          path="BWF/shop/product/registration/productReviewProductLabel"
          class="font-medium"
        />
        <ProductRegistrationProductCard class="w-60" :registration-product="selectedProduct" />
        <div>
          <button class="text-link flex items-center justify-center gap-x-2" @click="removeProduct">
            <CommonLabel path="BWF/shop/product/registration/registrationChangeLabel" />
            <SvgIcon name="Edit" class="h-3 w-3" />
          </button>
        </div>
      </div>

      <div class="flex flex-col" data-no-tracking>
        <p class="mb-4 font-medium">
          <CommonLabel path="BWF/shop/product/registration/productReviewAddressLabel" />
        </p>

        <span v-if="activeSalutation?.value?.includes('mr')"
          >{{ activeSalutation?.label }}&nbsp;</span
        >
        <div v-if="isLoggedIn">
          <div v-if="isDummyUser(user)" class="mb-4">
            <p>
              <span>{{ registrationState?.["bri_firstname"] }}&nbsp;</span>
              <span>{{ registrationState?.["bri_lastname"] }}</span>
            </p>
            <p class="mt-4">
              <CommonLabel path="BWF/shop/account/overview/noAddressAvailable" />
            </p>
          </div>

          <AccountAddressCard
            :key="userDefaultBillingAddress.id"
            :address="userDefaultBillingAddress"
            :button-styles="'underline text-icon-link text-base '"
            :show-title="false"
            is-billing
            :shipping-edit-enabled="false"
          />
        </div>

        <div v-else>
          <p>
            <span>{{ registrationState?.["bri_firstname"] }}&nbsp;</span>
            <span>{{ registrationState?.["bri_lastname"] }}</span>
          </p>
          <p>
            <span>{{ registrationState?.["bri_street1"] }}</span>
          </p>
          <p v-if="registrationState?.bri_additional_address_line_1">
            <span>{{ registrationState?.bri_additional_address_line_1 }}</span>
          </p>
          <p v-if="registrationState?.['bri_postalcode'] || registrationState?.['bri_city']">
            <span v-if="registrationState?.['bri_postalcode']"
              >{{ registrationState?.["bri_postalcode"] }}&nbsp;</span
            >
            <span v-if="registrationState?.['bri_city']">{{
              registrationState?.["bri_city"]
            }}</span>
          </p>
          <p v-if="activeCountry">
            <span>{{ activeCountry.label }}</span>
          </p>

          <p class="mt-4">
            <span>{{ registrationState?.["bri_email"] }}</span>
          </p>

          <button
            class="text-link mt-4 flex items-center justify-center gap-x-2"
            @click="$emit('change-personal-data')"
          >
            <CommonLabel path="BWF/shop/product/registration/registrationChangeLabel" />
            <SvgIcon name="Edit" class="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>

    <slot></slot>

    <Checkbox v-model="registrationConsent">
      <CommonLabel path="BWF/shop/product/registration/registrationConsent"></CommonLabel>
    </Checkbox>

    <button
      class="btn btn-blue w-full self-start md:w-auto"
      :disabled="!captchaSolution || !registrationConsent"
      @click="$emit('submit-registration')"
    >
      <CommonLabel path="BWF/shop/product/registration/productRegistrationCompleteLabel" />
    </button>
  </div>
</template>

<script setup lang="ts">
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import useProductRegistrationState from "~/composables/shop/useProductRegistrationState";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import useProductRegistrationPersonalInformation from "~/composables/shop/useProductRegistrationPersonalInformation";
import { Checkbox } from "~/components/clarity-ui/Checkbox";
import AccountAddressCard from "~/templates/components/shop/account/AccountAddressCard.vue";
import { isDummyUser } from "~/utils/helper/shop/user";
import { useProductRegistrationNewsletter } from "~/composables/shop/useProductRegistrationNewsletter";

defineProps(["captchaSolution"]);
const emits = defineEmits(["change-personal-data", "submit-registration"]);

const { userDefaultBillingAddress, isLoggedIn, user } = useUser();
watch(userDefaultBillingAddress, () => {
  emits("change-personal-data");
});

const { newsletterDoiCheck } = useProductRegistrationNewsletter();

const registrationConsent = ref(false);

const { selectableSalutations, selectableCountries } =
  await useProductRegistrationPersonalInformation();

const { allProducts } = await useProductRegistration();
const selectedProduct = computed(() => {
  return allProducts.value?.find((product) => product.id === registrationState?.value?.["id"]);
});
const { registrationState, removeProduct } = await useProductRegistrationState();

const activeSalutation = computed(() => {
  return selectableSalutations?.find(
    (salutation) => salutation.value === registrationState?.value?.["bri_salutation_opt"],
  );
});
const activeCountry = computed(() => {
  return selectableCountries.find(
    (country) => country?.value === registrationState?.value?.["bri_country"],
  );
});

onMounted(() => {
  window.scrollTo(0, 0);
  newsletterDoiCheck(registrationState.value?.bri_email);
});
</script>
